import {
  RegistrationFormResponse,
  RegistrationMediaFile,
} from "../models/registration";

export type { RegistrationMediaFile };

export const HIDDEN_FORM_RESPONSE_KEYS = new Set([
  "id",
  "registration_id",
  "event_id",
  "created_at",
  "updated_at",
]);

export const FILE_FIELD_KEYS = new Set([
  "selfie",
  "id_upload",
  "timing_certificate",
  "timing_doc",
]);

export const FORM_RESPONSE_FIELD_LABELS: Record<string, string> = {
  first_name: "First Name",
  last_name: "Last Name",
  email: "Email",
  phone: "Phone",
  dob: "Date of Birth",
  gender: "Gender",
  selfie: "Selfie",
  tshirt_size: "T-Shirt Size",
  nationality: "Nationality",
  country: "Country",
  state: "State",
  city: "City",
  address: "Address",
  pincode: "Pincode",
  running_club: "Running Club",
  id_type: "ID Type",
  id_number: "ID Number",
  id_upload: "ID Upload",
  timing_certificate: "Timing Certificate",
  timing_doc: "Timing Document",
  timing_certificate_category: "Race Category",
  completed_race_hours: "Completed Race (Hours)",
  completed_race_minutes: "Completed Race (Minutes)",
  completed_race_seconds: "Completed Race (Seconds)",
  emergency_name: "Emergency Contact",
  emergency_relation: "Emergency Relation",
  emergency_phone: "Emergency Phone",
  blood_group: "Blood Group",
  medical_conditions: "Medical Conditions",
  accept_terms: "Terms Accepted",
};

const FIELD_DISPLAY_ORDER: string[] = [
  "first_name",
  "last_name",
  "email",
  "phone",
  "dob",
  "gender",
  "tshirt_size",
  "blood_group",
  "nationality",
  "running_club",
  "medical_conditions",
  "country",
  "state",
  "city",
  "address",
  "pincode",
  "id_type",
  "id_number",
  "selfie",
  "id_upload",
  "timing_certificate",
  "timing_certificate_category",
  "completed_race_hours",
  "completed_race_minutes",
  "completed_race_seconds",
  "timing_doc",
  "emergency_name",
  "emergency_relation",
  "emergency_phone",
  "accept_terms",
];

const ID_TYPE_LABELS: Record<string, string> = {
  aadhaar: "Aadhaar Card",
  dl: "Driving License",
  passport: "Passport",
  voter_id: "Voter ID",
};

export function hasFormResponseValue(value: unknown, key?: string): boolean {
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (key === "accept_terms" && value === false) return false;
  if (typeof value === "boolean") return true;
  return true;
}

export function isMediaFileArray(value: unknown): value is RegistrationMediaFile[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === "object" &&
    value[0] !== null &&
    ("url" in value[0] || "thumbnail" in value[0])
  );
}

export function normalizeMediaFiles(value: unknown): RegistrationMediaFile[] {
  if (isMediaFileArray(value)) return value;
  if (typeof value === "string" && value.startsWith("http")) {
    return [{ url: value, thumbnail: value, name: "File" }];
  }
  return [];
}

export function getFormResponseLabel(key: string): string {
  return (
    FORM_RESPONSE_FIELD_LABELS[key] ??
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function formatFormResponseScalar(
  key: string,
  value: string | number | boolean,
): string {
  if (key === "id_type" && typeof value === "string") {
    return ID_TYPE_LABELS[value] ?? value;
  }
  if (key === "timing_certificate_category" && typeof value === "string") {
    return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

export function getDisplayableFormResponseEntries(
  fr: RegistrationFormResponse,
): { key: string; value: unknown }[] {
  const record = fr as Record<string, unknown>;
  const keysInData = Object.keys(record).filter(
    (key) =>
      !HIDDEN_FORM_RESPONSE_KEYS.has(key) && hasFormResponseValue(record[key], key),
  );

  const ordered = FIELD_DISPLAY_ORDER.filter((key) => keysInData.includes(key));
  const remainder = keysInData.filter((key) => !FIELD_DISPLAY_ORDER.includes(key));

  return [...ordered, ...remainder].map((key) => ({
    key,
    value: record[key],
  }));
}
